import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';
import {
  Bell, BellRing, Check, CheckCheck, Loader2,
  Volume2, VolumeX, X,
} from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { NotificationItem } from './admin/NotificationItem';

const NOTIFICATION_SOUND = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdH2NlZmTi4J6dXV4gImQkY+LhoF9eXp9gYWIiomGgn59fH1/goWHiIiGg4B9fHx9f4KFh4iIhoSBfn18fX+ChYeIiIaDgH5+fX5/goWHh4eFg4F/fn5+f4KFhoeGhIKAfn5+f4GEhoeGhYOBf35+fn+BhIaGhoWDgX9+fn5/gYSGhoaFg4F/fn5+f4GEhoaGhYOBf35+fn+BhIaGhoaFg4F/fn5+f4GEhoaGhYOBf35+fn+BhIaGhoWDgX9+fn5/gYSGhoaFg4F/fn5+f4GEhoaGhYOBf35+fn+BhIaGhoWDgX9+fn5/gYSGhoaFg4F/fn5+fn+BhIWGhYSCgH9+fn5/gYSFhoWEgoB/fn5+f4GEhYaFhIKAf35+fn+BhIWGhYSCgH9+fn5/gYSFhoWEgoB/fn5+f4GEhYaFhIKAf35+fn+BhIWGhYSCgH9+fn5/gYSFhoWEgoB/fn5+f4GEhYaFhIKAf35+fn+BhIWGhYSCgH9+fn5/gYSFhoWEgoB/fn5+';
const JACKPOT_SOUND = NOTIFICATION_SOUND;

const TYPE_LABELS = {
  transaction: 'Nueva Transaccion',
  withdrawal: 'Solicitud de Retiro',
  user: 'Nuevo Usuario',
  scraper_error: 'Error del Sistema',
  prediction_hit_3: 'JACKPOT! 3/3 Aciertos',
  prediction_hit_2: 'Excelente! 2/3 Aciertos',
  prediction_hit_1: '1/3 Acierto',
};

const AdminNotifications = () => {
  const { token } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [permissionState, setPermissionState] = useState('default');
  const previousUnreadCount = useRef(0);
  const audioRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => { audioRef.current = new Audio(NOTIFICATION_SOUND); audioRef.current.volume = 0.5; }, []);

  useEffect(() => {
    const handleClickOutside = (e) => { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setIsOpen(false); };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useEffect(() => {
    if ('Notification' in window) {
      setPermissionState(Notification.permission);
      setPushEnabled(localStorage.getItem('adminPushEnabled') === 'true');
      setSoundEnabled(localStorage.getItem('adminSoundEnabled') !== 'false');
    }
  }, []);

  const requestPermission = async () => {
    if (!('Notification' in window)) { toast.error('Tu navegador no soporta notificaciones'); return; }
    try {
      const permission = await Notification.requestPermission();
      setPermissionState(permission);
      if (permission === 'granted') {
        setPushEnabled(true);
        localStorage.setItem('adminPushEnabled', 'true');
        toast.success('Notificaciones push activadas');
      } else { toast.error('Permiso denegado'); }
    } catch (e) { toast.error('Error al solicitar permiso'); }
  };

  const togglePush = () => { if (permissionState !== 'granted') requestPermission(); else { const n = !pushEnabled; setPushEnabled(n); localStorage.setItem('adminPushEnabled', String(n)); } };
  const toggleSound = () => { const n = !soundEnabled; setSoundEnabled(n); localStorage.setItem('adminSoundEnabled', String(n)); };

  const playSound = useCallback(() => {
    if (soundEnabled && audioRef.current) { audioRef.current.currentTime = 0; audioRef.current.play().catch(() => {}); }
  }, [soundEnabled]);

  const showBrowserNotification = useCallback((n) => {
    if (!pushEnabled || permissionState !== 'granted') return;
    const notif = new Notification(TYPE_LABELS[n.type] || 'Nueva Notificacion', {
      body: n.message, icon: '/favicon.svg', tag: n.id, silent: true,
    });
    notif.onclick = () => { window.focus(); notif.close(); };
    setTimeout(() => notif.close(), 5000);
  }, [pushEnabled, permissionState]);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    try {
      const res = await axios.get(`${API}/admin/notifications?limit=20`, { headers: { Authorization: `Bearer ${token}` } });
      const newList = res.data.notifications || [];
      const newUnread = res.data.unread_count || 0;

      if (newUnread > previousUnreadCount.current && previousUnreadCount.current > 0) {
        const newItems = newList.filter(n => !n.read && !notifications.find(e => e.id === n.id));
        newItems.forEach(n => {
          showBrowserNotification(n);
          if (n.type === 'prediction_hit_3') toast.success('JACKPOT! 3/3 en ' + (n.lottery_name || ''), { duration: 10000 });
          else if (n.type === 'prediction_hit_2') toast.success('2/3 Aciertos en ' + (n.lottery_name || ''), { duration: 6000 });
        });
        if (newItems.length > 0) playSound();
      }
      previousUnreadCount.current = newUnread;
      setNotifications(newList);
      setUnreadCount(newUnread);
    } catch (e) { console.error('Error fetching notifications:', e); }
  }, [token, notifications, showBrowserNotification, playSound]);

  useEffect(() => { fetchNotifications(); const i = setInterval(fetchNotifications, 30000); return () => clearInterval(i); }, [fetchNotifications]);

  const markAsRead = async (id) => {
    try {
      await axios.post(`${API}/admin/notifications/${id}/read`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications(p => p.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(p => Math.max(0, p - 1));
      previousUnreadCount.current = Math.max(0, previousUnreadCount.current - 1);
    } catch (e) { console.error('Error marking as read:', e); }
  };

  const markAllAsRead = async () => {
    setLoading(true);
    try {
      await axios.post(`${API}/admin/notifications/read-all`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setNotifications(p => p.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
      previousUnreadCount.current = 0;
    } catch (e) { console.error('Error:', e); }
    finally { setLoading(false); }
  };

  const deleteNotification = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/admin/notifications/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      const n = notifications.find(x => x.id === id);
      setNotifications(p => p.filter(x => x.id !== id));
      if (n && !n.read) { setUnreadCount(p => Math.max(0, p - 1)); previousUnreadCount.current = Math.max(0, previousUnreadCount.current - 1); }
    } catch (e) { console.error('Error:', e); }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button onClick={() => setIsOpen(!isOpen)} className="relative p-2 rounded-lg bg-gray-800 text-gray-400 hover:text-white transition-colors" data-testid="admin-notifications-bell">
        <Bell size={20} className={unreadCount > 0 ? "text-amber-400" : ""} />
        <span className={`absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center transition-opacity ${unreadCount > 0 ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      </button>

      {isOpen && (
        <div className="absolute right-0 top-12 w-96 bg-gray-900 border border-gray-700 rounded-xl shadow-2xl z-50 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800">
            <div className="flex items-center gap-2">
              <Bell size={18} className="text-amber-400" />
              <span className="font-semibold text-white">Notificaciones</span>
              {unreadCount > 0 && <Badge className="bg-red-500/20 text-red-400 text-xs">{unreadCount} nuevas</Badge>}
            </div>
            <div className="flex items-center gap-1">
              <button onClick={toggleSound} className={`p-1.5 rounded hover:bg-gray-700 ${soundEnabled ? 'text-green-400' : 'text-gray-500'}`}>
                {soundEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
              </button>
              <button onClick={togglePush} className={`p-1.5 rounded hover:bg-gray-700 ${pushEnabled && permissionState === 'granted' ? 'text-green-400' : 'text-gray-500'}`} data-testid="push-toggle">
                <BellRing size={16} />
              </button>
              <button onClick={() => setIsOpen(false)} className="p-1.5 rounded hover:bg-gray-700 text-gray-400 ml-2"><X size={16} /></button>
            </div>
          </div>

          {permissionState === 'default' && (
            <div className="px-4 py-3 bg-amber-500/10 border-b border-amber-500/20">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center flex-shrink-0"><BellRing size={20} className="text-amber-400" /></div>
                <div className="flex-1">
                  <p className="text-sm text-white font-medium">Activar notificaciones push</p>
                  <p className="text-xs text-gray-400">Recibe alertas con el navegador minimizado</p>
                </div>
                <Button size="sm" onClick={requestPermission} className="bg-amber-500 hover:bg-amber-400 text-black text-xs">Activar</Button>
              </div>
            </div>
          )}

          {pushEnabled && permissionState === 'granted' && (
            <div className="px-4 py-2 bg-green-500/10 border-b border-green-500/20 flex items-center gap-2">
              <Check size={14} className="text-green-400" /><span className="text-xs text-green-400">Push activas</span>
            </div>
          )}

          {unreadCount > 0 && (
            <div className="px-4 py-2 border-b border-gray-800 flex justify-end">
              <Button variant="ghost" size="sm" onClick={markAllAsRead} disabled={loading} className="text-xs text-gray-400 hover:text-white flex items-center gap-1">
                {loading ? <Loader2 size={14} className="animate-spin" /> : <><CheckCheck size={14} /><span>Marcar todas como leidas</span></>}
              </Button>
            </div>
          )}

          <div className="max-h-[350px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <Bell size={40} className="text-gray-600 mb-3" />
                <p className="text-gray-500 text-sm">No hay notificaciones</p>
              </div>
            ) : notifications.map(n => (
              <NotificationItem key={n.id} notification={n} onMarkRead={markAsRead} onDelete={deleteNotification} />
            ))}
          </div>

          <div className="px-4 py-2 border-t border-gray-800 bg-gray-900/50 flex items-center justify-between">
            <p className="text-xs text-gray-500">Actualizando cada 30s</p>
            <div className="flex items-center gap-2">
              <Volume2 size={12} className={soundEnabled ? "text-green-400" : "text-gray-600 opacity-0"} />
              <BellRing size={12} className={pushEnabled && permissionState === 'granted' ? "text-green-400" : "text-gray-600 opacity-0"} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNotifications;
